import { Set } from 'immutable';
import { calcDimensions, getPieData, ICoordinateSystem, IMargin } from '@opengov/data-viz';
import { IDataValuesFormat, OG_META_DATA_KEY } from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import Big from 'big.js';

import { getCategoriesFromChartData } from './categories';
import { assembleAllColors } from './chartCalculations';
import { IChartConfigurator, IChartDimensions, IChartMargin, IPieChartCalculations } from './types';

const getSortedCategoriesForScale = (combinedData) => {
  const categoryToValue = combinedData.reduce((acc, d) => {
    acc[d.name] = d.value;
    return acc;
  }, {});

  return Object.keys(categoryToValue).sort((a, b) => categoryToValue[b] - categoryToValue[a]);
};

type IPieData = {
  name: string;
  value: number;
  data: {
    meta: {
      range: string[];
      value: number;
      amount: number;
      percentTotal: number;
      total: number;
    };
  };
};

// our Pie charts reduce categories across all rows
export const dataToPie = (
  data: IDataValuesFormat[],
  categories: Set<string> | string[],
  isPercentOfTotal: boolean
): IPieData[] => {
  return (categories as string[]).reduce((accumulator, category) => {
    const value: Big = data.reduce((acc, d) => {
      // the first transform for % query returns values as % per period.
      // For Pie we need the actual values because we want the % across all periods
      const val = isPercentOfTotal ? d[OG_META_DATA_KEY].value[category] : d[category];

      // this period might not have that category
      if (!isBlank(val)) {
        return acc.plus(val);
      }

      return acc;
    }, Big(0));

    const total = data.reduce((acc, d) => {
      const aggTotal = d[OG_META_DATA_KEY].aggregateTotal;
      if (isBlank(aggTotal)) {
        return acc;
      }
      return acc.plus(aggTotal);
    }, Big(0));

    const percentTotal = value.eq(0) || total.eq(0) ? 0 : Number(value.div(total).toFixed());
    const first = data[0];
    const last = data[data.length - 1];
    const testIfTooLarge = value.toFixed() === Number(value.toFixed()).toString();

    if (!testIfTooLarge) {
      throw new Error('Numbers are larger than Number.MAX_SAFE_INTEGER');
    }

    accumulator.push({
      name: category,
      // we have to give back a Number or else D3 will not know what to do with it
      value: Number(value.toFixed()),
      data: {
        meta: {
          range: [first, last],
          value: isPercentOfTotal ? percentTotal : value.toFixed(),
          amount: value.toFixed(),
          percentTotal: percentTotal,
          total: total.toFixed()
        }
      }
    });
    return accumulator;
  }, []);
};

export const createPieChartCalculations = (
  config: IChartConfigurator,
  isPercentOfTotal: boolean,
  margin: IMargin,
  customColors?: { [key: string]: string }
): IPieChartCalculations => {
  const { data, width, sortOrder } = config;
  const { coordinateSystem, dimensions } = makePieCoordinateSystems(margin, width);
  const categories = data && data.length > 0 ? getCategoriesFromChartData(data) : Set<string>();
  const combinedData = dataToPie(data, categories, isPercentOfTotal);
  const pieData = getPieData(combinedData, categories, sortOrder, (d: any) => d.value);
  const sortedCategoriesForScale = getSortedCategoriesForScale(combinedData);
  const chartColors = assembleAllColors(sortedCategoriesForScale, customColors || {});
  // pull to sort from the d3 pie struct
  const sortedCategories = pieData.data.sort((a, b) => a.index - b.index).map((i) => i.data.name);

  return {
    coordinateSystem,
    data,
    dimensions,
    margin,
    categories: sortedCategories,
    colorMap: chartColors as any,
    pieData,
    showAsPercentOfTotal: isPercentOfTotal
  };
};

export const makePieCoordinateSystems = (
  margin,
  width = 1200
): { coordinateSystem: ICoordinateSystem; margin: IChartMargin; dimensions: IChartDimensions } => {
  const coordinateSystem = { x: 0, y: 0, width, height: 360 };
  return {
    coordinateSystem,
    margin,
    dimensions: calcDimensions(margin, coordinateSystem.width, coordinateSystem.height)
  };
};
